/**
 * Video Page - Versicherten Video Room
 *
 * Flow: Waiting Room → Video Room → Session End Screen
 *
 * Uses LiveKit React prefabs with E2E encryption for BMV-A compliance.
 */

import { useState, useCallback, useEffect, Suspense, lazy } from "react";
import { useParams, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useQuery, useAction, useMutation } from "convex/react";
import { api } from "@jourfx/convex/client";
import { WaitingRoom } from "../components/video/WaitingRoom";
import { SessionEndScreen } from "../components/video/SessionEndScreen";
import { PrivacyNoticeModal } from "../components/consent/PrivacyNoticeModal";
import { useSession } from "../hooks/useSession";

// Type for Convex Id
type Id<T extends string> = string & { __tableName: T };

type VideoState = "waiting" | "connecting" | "connected" | "ended";

interface SessionData {
  duration: number;
  examinerName?: string;
  examinerAvatarUrl?: string;
  appointmentDate?: Date;
}

const LazyVideoRoom = lazy(() =>
  import("../components/video/VideoRoom")
    .then((m) => ({ default: m.VideoRoom }))
    .catch(() => {
      const key = "chunk-reload:/video";
      if (!sessionStorage.getItem(key)) {
        sessionStorage.setItem(key, "1");
        window.location.reload();
      }
      throw new Error("Video room chunk unavailable — page will reload");
    })
);

export function VideoPage() {
  const { t } = useTranslation("video");
  const { appointmentId } = useParams({ from: "/video/$appointmentId" });
  const navigate = useNavigate();
  const { session } = useSession(appointmentId);
  const apiRef = api as any;

  // Suppress browser translate badge on video page
  useEffect(() => {
    document.documentElement.setAttribute("translate", "no");
    document.documentElement.classList.add("notranslate");
    return () => {
      document.documentElement.removeAttribute("translate");
      document.documentElement.classList.remove("notranslate");
    };
  }, []);

  const [videoState, setVideoState] = useState<VideoState>("waiting");
  const [token, setToken] = useState<string | null>(null);
  const [serverUrl, setServerUrl] = useState<string | null>(null);
  const [e2eeSharedKey, setE2eeSharedKey] = useState<string | null>(null);
  const [forceLegacySignalPath, setForceLegacySignalPath] = useState(true);
  const [sessionData, setSessionData] = useState<SessionData | null>(null);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [showExaminerEndedNotice, setShowExaminerEndedNotice] = useState(false);
  const [examinerNoticeCountdown, setExaminerNoticeCountdown] = useState(3);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);

  // Get appointment info for versicherte/r (uses appointment session)
  const appointment = useQuery(
    api.appointments.index.getForVersicherter,
    appointmentId && session?.sessionToken
      ? {
          appointmentId: appointmentId as Id<"appointments">,
          sessionToken: session.sessionToken,
        }
      : "skip"
  );

  const generateToken = useAction(api.video.token.generateToken);
  const recordPrivacyAcknowledgement = useMutation(
    api.consents.index.recordPrivacyAcknowledgement
  );
  const privacyConsent = useQuery(
    api.consents.index.getPrivacyAcknowledgement,
    appointmentId && session?.sessionToken
      ? {
          appointmentId: appointmentId as Id<"appointments">,
          sessionToken: session.sessionToken,
        }
      : "skip"
  );
  const prepWindow = useQuery(
    apiRef.video.admission.getPrepWindowStatus,
    appointmentId && session?.sessionToken
      ? {
          appointmentId: appointmentId as Id<"appointments">,
          sessionToken: session.sessionToken,
        }
      : "skip"
  ) as { allowed: boolean; opensAt: number | null; msUntilOpen: number | null } | undefined;

  const connectToVideoRoom = useCallback(async () => {
    if (!appointmentId || !session?.sessionToken) {
      navigate({ to: "/", search: { id: appointmentId } } as any);
      return;
    }

    setVideoState("connecting");
    setConnectionError(null);

    try {
      const result = await generateToken({
        appointmentId: appointmentId as Id<"appointments">,
        sessionToken: session.sessionToken,
      });
      setToken(result.token);
      setServerUrl(result.url);
      setE2eeSharedKey(result.e2eeKey);
      setForceLegacySignalPath(result.signalPath ? result.signalPath === "v0" : true);
      setVideoState("connected");
    } catch (err: any) {
      const errorCode = err?.data?.code;
      if (errorCode === "CONSENT_REQUIRED") {
        setShowPrivacyModal(true);
        setVideoState("waiting");
        return;
      }

      console.error("Failed to get token:", err);
      const errorData = err?.data;
      setConnectionError(errorData?.message || t("errors.connectionFailed"));
      // Stay on connecting screen with error instead of looping back to waiting room
      setVideoState("connecting");
    }
  }, [appointmentId, generateToken, navigate, session?.sessionToken, t]);

  // Handle versicherte/r admitted from waiting room
  const handleAdmitted = useCallback(async () => {
    if (!appointmentId) return;
    if (!session?.sessionToken) {
      navigate({ to: "/", search: { id: appointmentId } } as any);
      return;
    }

    if (privacyConsent?.accepted === false && !privacyAccepted) {
      setShowPrivacyModal(true);
      return;
    }

    await connectToVideoRoom();
  }, [
    appointmentId,
    connectToVideoRoom,
    navigate,
    privacyAccepted,
    privacyConsent?.accepted,
    session?.sessionToken,
  ]);

  const handlePrivacyAccepted = useCallback(async () => {
    if (!appointmentId || !session?.sessionToken) {
      navigate({ to: "/", search: { id: appointmentId } } as any);
      return;
    }

    try {
      await recordPrivacyAcknowledgement({
        appointmentId: appointmentId as Id<"appointments">,
        sessionToken: session.sessionToken,
        version: "v1",
        userAgent: window.navigator.userAgent,
      });
      setPrivacyAccepted(true);
      setShowPrivacyModal(false);
      await connectToVideoRoom();
    } catch (err: any) {
      console.error("Failed to record privacy acknowledgement:", err);
      const errorData = err?.data;
      setShowPrivacyModal(false);
      setConnectionError(errorData?.message || t("errors.connectionFailed"));
      setVideoState("connecting");
    }
  }, [
    appointmentId,
    connectToVideoRoom,
    navigate,
    recordPrivacyAcknowledgement,
    session?.sessionToken,
    t,
  ]);

  const handlePrivacyDeclined = useCallback(() => {
    setShowPrivacyModal(false);
    navigate({ to: "/appointment/$appointmentId", params: { appointmentId } } as any);
  }, [appointmentId, navigate]);

  // Handle leaving waiting room
  const handleLeaveWaiting = useCallback(() => {
    navigate({ to: "/" });
  }, [navigate]);

  // Handle session end
  const handleSessionEnd = useCallback((duration: number, endedByExaminer: boolean) => {
    setSessionData({
      duration,
      examinerName: appointment?.gutachterName,
      examinerAvatarUrl: appointment?.gutachterAvatarUrl ?? undefined,
      appointmentDate: appointment?.scheduledAt
        ? new Date(appointment.scheduledAt)
        : new Date(),
    });
    if (endedByExaminer) {
      setShowExaminerEndedNotice(true);
    } else {
      setVideoState("ended");
    }
  }, [appointment]);

  const handleContinueToReview = useCallback(() => {
    setShowExaminerEndedNotice(false);
    setVideoState("ended");
  }, []);

  useEffect(() => {
    if (!showExaminerEndedNotice) {
      return;
    }

    setExaminerNoticeCountdown(3);
    const timer = setInterval(() => {
      setExaminerNoticeCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          handleContinueToReview();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      clearInterval(timer);
    };
  }, [showExaminerEndedNotice, handleContinueToReview]);

  // System settings
  const testingMode = useQuery(api.admin.systemSettings.getTestingMode);
  const debugMode = testingMode?.enabled ?? false;
  const feedbackSetting = useQuery(api.admin.systemSettings.getFeedbackEnabled);
  const feedbackEnabled = feedbackSetting?.enabled ?? true;

  // Error state - no appointment ID
  if (!appointmentId) {
    return (
      <div className="min-h-screen bg-[var(--stone-50)] flex items-center justify-center">
        <div className="text-[var(--stone-600)]">{t("errors.appointmentNotFound")}</div>
      </div>
    );
  }

  if (showExaminerEndedNotice && sessionData) {
    return (
      <div className="min-h-screen bg-[var(--stone-950)] flex items-center justify-center p-4">
        <div className="w-full max-w-md rounded-2xl bg-white shadow-lg p-8 space-y-5 text-center">
          <h1 className="text-xl font-semibold text-[var(--stone-900)]">
            {t("sessionEnd.examinerEndedNotice.title")}
          </h1>
          <p className="text-[var(--stone-600)]">{t("sessionEnd.examinerEndedNotice.message")}</p>
          <p className="text-sm text-[var(--stone-500)]">
            {t("sessionEnd.examinerEndedNotice.autoContinue", {
              seconds: examinerNoticeCountdown,
            })}
          </p>
          <button
            onClick={handleContinueToReview}
            className="w-full px-4 py-3 rounded-xl bg-[var(--sage-600)] text-white font-semibold hover:bg-[var(--sage-700)] transition-colors"
          >
            {t("sessionEnd.examinerEndedNotice.continue")}
          </button>
        </div>
      </div>
    );
  }

  if (videoState === "waiting") {
    if (!debugMode && prepWindow && !prepWindow.allowed) {
      const totalSeconds = Math.max(0, Math.ceil((prepWindow.msUntilOpen || 0) / 1000));
      const days = Math.floor(totalSeconds / 86400);
      const hours = Math.floor((totalSeconds % 86400) / 3600);
      const mins = Math.floor((totalSeconds % 3600) / 60);
      const parts: string[] = [];
      if (days > 0) parts.push(`${days} ${days === 1 ? "Tag" : "Tage"}`);
      if (hours > 0) parts.push(`${hours} ${hours === 1 ? "Stunde" : "Stunden"}`);
      parts.push(`${mins} ${mins === 1 ? "Minute" : "Minuten"}`);
      return (
        <div className="min-h-screen client-gradient-bg flex items-center justify-center p-4">
          <div className="client-card w-full max-w-md p-6 text-center space-y-4">
            <h1 className="text-2xl font-bold text-[var(--stone-800)]">Warteraum noch gesperrt</h1>
            <p className="text-[var(--stone-600)]">
              Der Warteraum ist 30 Minuten vor dem Termin verfügbar.
            </p>
            <p className="text-lg font-semibold text-[var(--sage-700)]">
              Freigabe in {parts.join(", ")}
            </p>
            <button
              onClick={() =>
                navigate({ to: "/appointment/$appointmentId", params: { appointmentId } } as any)
              }
              className="w-full px-4 py-3 rounded-xl bg-[var(--stone-100)] text-[var(--stone-700)] font-medium hover:bg-[var(--stone-200)] transition-colors"
            >
              Zurück zum Termin
            </button>
          </div>
        </div>
      );
    }

    return (
      <>
        <PrivacyNoticeModal
          open={showPrivacyModal}
          title={t("privacy.title")}
          body={t("privacy.body")}
          acceptLabel={t("privacy.accept")}
          declineLabel={t("privacy.decline")}
          onAccept={() => {
            void handlePrivacyAccepted();
          }}
          onDecline={handlePrivacyDeclined}
        />
        <WaitingRoom
          appointmentId={appointmentId}
          onAdmitted={handleAdmitted}
          onLeave={handleLeaveWaiting}
          debug={debugMode}
        />
      </>
    );
  }

  // Connecting state
  if (videoState === "connecting" || !token || !serverUrl || !e2eeSharedKey) {
    return (
      <div className="min-h-screen bg-stone-950 flex items-center justify-center">
        {connectionError ? (
          <div className="text-center space-y-4 p-6 max-w-md">
            <div className="text-red-400 text-lg font-medium">{t("errors.connectionFailed")}</div>
            <p className="text-stone-400 text-sm">{connectionError}</p>
            <button
              onClick={handleAdmitted}
              className="px-6 py-3 rounded-xl bg-white text-stone-900 font-semibold hover:bg-stone-100 transition-colors"
            >
              {t("errors.retry")}
            </button>
          </div>
        ) : (
          <div className="text-white animate-pulse">{t("room.connecting")}</div>
        )}
      </div>
    );
  }

  // Session ended state
  if (videoState === "ended" && sessionData) {
    return (
      <SessionEndScreen
        appointmentId={appointmentId}
        sessionDuration={sessionData.duration}
        examinerName={sessionData.examinerName}
        examinerAvatarUrl={sessionData.examinerAvatarUrl}
        appointmentDate={sessionData.appointmentDate}
        feedbackEnabled={feedbackEnabled}
      />
    );
  }

  // Video room state
  return (
    <Suspense
      fallback={<div className="min-h-screen bg-stone-950 flex items-center justify-center text-white animate-pulse">{t("room.connecting")}</div>}
    >
      <LazyVideoRoom
        serverUrl={serverUrl}
        token={token}
        e2eeSharedKey={e2eeSharedKey}
        forceLegacySignalPath={forceLegacySignalPath}
        appointmentId={appointmentId}
        sessionToken={session?.sessionToken ?? ""}
        onSessionEnd={handleSessionEnd}
      />
    </Suspense>
  );
}
